import type { CarrierConnection, CarrierRequest, CarrierRequestView, CarrierWorkspace, CustomerDetail, CustomerSummary } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, FlaskConical, Pencil, Plus, Send } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, Checkbox, ErrorState, Input, LoadingState, Select, Sheet, Textarea, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { formatPhone, shortDate } from "../theme";

const LINES = ["auto", "home", "renters", "commercial_auto", "general_liability", "bop", "life", "health", "other"] as const;
const METHOD_IDS = ["api", "ivans", "teamup", "download_feed", "exported_files", "portal_automation", "document_ingestion", "undecided"] as const;
type Method = CarrierConnection["method"];

/** Plain-language connection state chip. Same labels everywhere: Demo / Not connected / Ready for setup / Test / Live. */
export function CarrierStateBadge({ state }: { state: CarrierConnection["status"] }) {
  const t = useT();
  const tone = state === "live" ? "green" : state === "test" ? "blue" : state === "demo" ? "amber" : state === "ready_for_setup" ? "blue" : "neutral";
  return <Badge tone={tone}>{t(`carriers.state.${state}`)}</Badge>;
}

export function RequestStatusBadge({ status }: { status: CarrierRequest["status"] }) {
  const t = useT();
  const tone = status === "imported" ? "green" : status === "failed" ? "red" : status === "needs_info" ? "amber" : status === "file_ready" || status === "downloading" || status === "processing" ? "blue" : "neutral";
  return <Badge tone={tone}>{t(`carriers.status.${status}`)}</Badge>;
}

export function CarriersScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const [params] = useSearchParams();
  const q = useQuery({ queryKey: ["carriers"], queryFn: async () => (await callAction<"agency.carrier.list", CarrierWorkspace>("agency.carrier.list", {})).result, refetchInterval: 10_000 });
  const [editing, setEditing] = useState<CarrierConnection | "new" | null>(null);
  const methodLabel = (m: Method) => q.data?.methods.find((x) => x.id === m)?.label[locale] ?? m;

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("carriers.title")}</h1>
          <p className="text-ink/60 max-w-2xl">{t("carriers.subtitle")}</p>
        </div>
        <Button onClick={() => setEditing("new")}><Plus className="h-4 w-4" />{t("carriers.add")}</Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink/55">
            <tr>
              <th className="px-4 py-3">{t("carriers.col.carrier")}</th>
              <th className="px-4 py-3">{t("carriers.col.method")}</th>
              <th className="px-4 py-3 hidden md:table-cell">{t("carriers.col.scope")}</th>
              <th className="px-4 py-3 hidden lg:table-cell">{t("carriers.col.formats")}</th>
              <th className="px-4 py-3">{t("carriers.col.status")}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {d.carriers.map((c) => (
              <tr key={c.id} className={cn("border-t border-ink/5", c.simulated && "bg-accent/5")}>
                <td className="px-4 py-2">
                  <p className="font-semibold flex flex-wrap items-center gap-2">{c.name} {c.simulated ? <Badge tone="amber"><FlaskConical className="h-3 w-3" />{t("carriers.simulated")}</Badge> : null}</p>
                  <p className="text-xs text-ink/55">{t(`carriers.kind.${c.kind}`)}{c.agencyCode ? ` · ${t("carriers.agencyCode")} ${c.agencyCode}` : ""}{c.simulated ? ` · ${t("carriers.simulatedNote")}` : ""}</p>
                </td>
                <td className="px-4 py-2">{methodLabel(c.method)}</td>
                <td className="px-4 py-2 hidden md:table-cell text-ink/70">{c.states.join(", ") || "—"} · {c.lines.map((l) => l.replace(/_/g, " ")).join(", ") || "—"}</td>
                <td className="px-4 py-2 hidden lg:table-cell text-ink/70">{c.fileFormats.join(", ") || "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-col gap-1 items-start">
                    <CarrierStateBadge state={c.status} />
                    <span className="text-xs text-ink/55">{t(`carriers.verification.${c.verification}`)}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-right"><Button variant="outline" size="sm" onClick={() => setEditing(c)}><Pencil className="h-4 w-4" />{t("carriers.edit")}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <DemoWorkflowCard carriers={d.carriers} initialCustomerId={params.get("customerId") ?? ""} />
        <Card>
          <CardHeader title={t("carriers.requests")} />
          {d.requests.length === 0 ? <p className="px-5 pb-5 text-sm text-ink/55">{t("carriers.requests.none")}</p> : null}
          <ul className="divide-y divide-ink/5">
            {d.requests.slice(0, 12).map((r) => (
              <li key={r.id} className="px-5 py-2 flex items-center gap-2 text-sm">
                <div className="flex-1 min-w-0">
                  <Link to={`/carriers/requests/${r.id}`} className="font-semibold hover:underline">{r.customerName} · {t(`carriers.kind.${r.kind}`)}</Link>
                  <p className="text-xs text-ink/55 truncate">{r.carrierName} · {shortDate(r.updatedAt, locale)} {r.simulated ? `· ${t("carriers.simulated")}` : ""}</p>
                </div>
                <RequestStatusBadge status={r.status} />
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card>
        <CardHeader title={t("carriers.methods.title")} />
        <ul className="px-5 pb-5 grid gap-2 sm:grid-cols-2 text-sm">
          {d.methods.map((m) => (
            <li key={m.id} className="rounded-xl bg-surface p-3"><p className="font-semibold">{m.label[locale]}</p><p className="text-ink/65">{m.note[locale]}</p></li>
          ))}
        </ul>
      </Card>

      <CarrierSheet carrier={editing === "new" ? null : editing} open={editing !== null} onClose={() => setEditing(null)} methods={d.methods} users={config.users} />
    </div>
  );
}

function CarrierSheet({ carrier, open, onClose, methods, users }: { carrier: CarrierConnection | null; open: boolean; onClose: () => void; methods: CarrierWorkspace["methods"]; users: Array<{ id: string; name: string; role: string }> }) {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();
  const blank = { name: "", kind: "carrier" as CarrierConnection["kind"], naic: "", agencyCode: "", producerCodes: {} as Record<string, string>, states: "", lines: [] as string[], method: "undecided" as Method, fileFormats: "", portalUrl: "", verification: "not_verified" as CarrierConnection["verification"], autoImport: false, notes: "", requiredInfo: [] as CarrierConnection["requiredInfo"] };
  const initial = carrier ? { ...blank, name: carrier.name, kind: carrier.kind, naic: carrier.naic ?? "", agencyCode: carrier.agencyCode ?? "", producerCodes: carrier.producerCodes, states: carrier.states.join(", "), lines: [...carrier.lines], method: carrier.method, fileFormats: carrier.fileFormats.join(", "), portalUrl: carrier.portalUrl ?? "", verification: carrier.verification, autoImport: carrier.autoImport, notes: carrier.notes ?? "", requiredInfo: carrier.requiredInfo.map((r) => ({ ...r })) } : blank;
  const [form, setForm] = useState(initial);
  const [seeded, setSeeded] = useState<string | null>(carrier?.id ?? "new");
  const key = carrier?.id ?? "new";
  if (seeded !== key) { setSeeded(key); setForm(initial); }

  const save = useMutation({
    mutationFn: () => callAction<"agency.carrier.upsert", CarrierConnection>("agency.carrier.upsert", {
      id: carrier?.id, name: form.name, kind: form.kind, naic: form.naic || undefined, agencyCode: form.agencyCode || undefined, producerCodes: form.producerCodes,
      states: form.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean), lines: form.lines as CarrierConnection["lines"], method: form.method,
      fileFormats: form.fileFormats.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean), portalUrl: form.portalUrl || undefined, verification: form.verification, autoImport: form.autoImport, notes: form.notes || undefined,
      requiredInfo: form.requiredInfo.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), value: r.value?.trim() || undefined, source: r.source, documentId: r.documentId })),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["carriers"] }); toast({ title: t("carriers.saved"), tone: "success" }); onClose(); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const producers = users.filter((u) => u.role === "owner" || u.role === "agent" || u.role === "csr");

  return (
    <Sheet open={open} onClose={onClose} title={carrier ? `${t("carriers.edit")} · ${carrier.name}` : t("carriers.add")}>
      <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (form.name.trim()) save.mutate(); }}>
        {carrier ? <div className="flex items-center gap-2 text-sm"><CarrierStateBadge state={carrier.status} />{carrier.simulated ? <Badge tone="amber">{t("carriers.simulated")}</Badge> : null}<span className="text-xs text-ink/55">{t("carriers.statusNote")}</span></div> : <p className="text-xs text-ink/55">{t("carriers.statusNote")}</p>}
        <Input label={t("carriers.name")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Select label={t("common.kind")} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as CarrierConnection["kind"] })}>
            {(["carrier", "mga", "wholesaler"] as const).map((k) => <option key={k} value={k}>{t(`carriers.kind.${k}`)}</option>)}
          </Select>
          <Input label={t("carriers.naic")} value={form.naic} onChange={(e) => setForm({ ...form, naic: e.target.value })} />
        </div>
        <Input label={t("carriers.agencyCode")} value={form.agencyCode} onChange={(e) => setForm({ ...form, agencyCode: e.target.value })} />
        <div>
          <p className="mb-1 text-sm font-semibold text-ink/80">{t("carriers.producerCodes")}</p>
          <div className="space-y-2">
            {producers.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-sm"><span className="w-40 truncate">{u.name}</span><Input value={form.producerCodes[u.id] ?? ""} onChange={(e) => setForm({ ...form, producerCodes: { ...form.producerCodes, [u.id]: e.target.value } })} /></div>
            ))}
          </div>
        </div>
        <Input label={t("carriers.states")} value={form.states} onChange={(e) => setForm({ ...form, states: e.target.value })} placeholder="TX, NM" />
        <div>
          <p className="mb-1 text-sm font-semibold text-ink/80">{t("carriers.lines")}</p>
          <div className="flex flex-wrap gap-2">
            {LINES.map((l) => (
              <button type="button" key={l} onClick={() => setForm({ ...form, lines: form.lines.includes(l) ? form.lines.filter((x) => x !== l) : [...form.lines, l] })} className={cn("min-h-[36px] rounded-full border px-3 text-sm font-semibold", form.lines.includes(l) ? "border-primary bg-primary/10 text-primary-dark" : "border-ink/15 text-ink/70")} aria-pressed={form.lines.includes(l)}>{l.replace(/_/g, " ")}</button>
            ))}
          </div>
        </div>
        <Select label={t("carriers.method")} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as Method })}>
          {METHOD_IDS.map((m) => <option key={m} value={m}>{methods.find((x) => x.id === m)?.label[locale] ?? m}</option>)}
        </Select>
        <p className="text-xs text-ink/55 -mt-1">{methods.find((x) => x.id === form.method)?.note[locale]}</p>
        <Input label={t("carriers.formats")} value={form.fileFormats} onChange={(e) => setForm({ ...form, fileFormats: e.target.value })} placeholder="al3, csv" />
        <Input label={t("carriers.portalUrl")} value={form.portalUrl} onChange={(e) => setForm({ ...form, portalUrl: e.target.value })} />
        <Select label={t("carriers.verificationLabel")} value={form.verification} onChange={(e) => setForm({ ...form, verification: e.target.value as CarrierConnection["verification"] })}>
          {(["not_verified", "agency_confirmed", "documented"] as const).map((v) => <option key={v} value={v}>{t(`carriers.verification.${v}`)}</option>)}
        </Select>
        <Checkbox label={t("carriers.autoImport")} checked={form.autoImport} onChange={(v) => setForm({ ...form, autoImport: v })} />
        <div>
          <p className="text-sm font-semibold text-ink/80">{t("carriers.requiredInfo")}</p>
          <p className="text-xs text-ink/55 mb-2">{t("carriers.requiredInfo.help")}</p>
          <div className="space-y-2">
            {form.requiredInfo.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_120px] gap-2">
                <Input placeholder={t("carriers.requiredInfo.label")} value={r.label} onChange={(e) => setForm({ ...form, requiredInfo: form.requiredInfo.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <Input placeholder={t("carriers.requiredInfo.value")} value={r.value ?? ""} onChange={(e) => setForm({ ...form, requiredInfo: form.requiredInfo.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} />
                <Select value={r.source} onChange={(e) => setForm({ ...form, requiredInfo: form.requiredInfo.map((x, j) => (j === i ? { ...x, source: e.target.value as "agency" | "carrier_doc" | "unknown" } : x)) })}>
                  {(["unknown", "agency", "carrier_doc"] as const).map((s) => <option key={s} value={s}>{t(`carriers.source.${s}`)}</option>)}
                </Select>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, requiredInfo: [...form.requiredInfo, { label: "", value: "", source: "unknown" }] })}><Plus className="h-4 w-4" />{t("carriers.addRow")}</Button>
          </div>
        </div>
        <Textarea label={t("carriers.notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <Button type="submit" size="lg" className="w-full" loading={save.isPending}>{t("common.save")}</Button>
      </form>
    </Sheet>
  );
}

/** Pick a customer + policy and send a request to the simulated carrier. Only simulated carriers accept requests in this build. */
export function DemoWorkflowCard({ carriers, initialCustomerId, compact }: { carriers: CarrierConnection[]; initialCustomerId?: string; compact?: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const simulated = carriers.filter((c) => c.simulated);
  const [carrierId, setCarrierId] = useState(simulated[0]?.id ?? "");
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState(initialCustomerId ?? "");
  const [policyId, setPolicyId] = useState("");
  const [kind, setKind] = useState<CarrierRequest["kind"]>("policy_download");
  const search = useQuery({ queryKey: ["carrier-customer-search", q], queryFn: async () => (await callAction<"agency.customer.search", CustomerSummary[]>("agency.customer.search", { q, limit: 8 })).result, enabled: q.trim().length >= 2 && !customerId });
  const detail = useQuery({ queryKey: ["customer", customerId], queryFn: async () => (await callAction<"agency.customer.get", CustomerDetail>("agency.customer.get", { customerId })).result, enabled: !!customerId });
  const policies = useMemo(() => (detail.data?.policies ?? []).filter((p) => p.status !== "quoted"), [detail.data]);
  const effectivePolicy = policyId || policies[0]?.id || "";

  const go = useMutation({
    mutationFn: async () => {
      const created = await callAction<"agency.carrier.request.create", CarrierRequest>("agency.carrier.request.create", { carrierConnectionId: carrierId, customerId, policyId: effectivePolicy || undefined, kind, payload: {} });
      const submitted = await callAction<"agency.carrier.request.submit", CarrierRequestView>("agency.carrier.request.submit", { requestId: created.result.id });
      return submitted.result;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["carriers"] });
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      qc.invalidateQueries({ queryKey: ["today"] });
      toast({ title: t("carriers.demo.createdSubmitted"), tone: "success" });
      navigate(`/carriers/requests/${r.request.id}`);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  return (
    <Card className={cn(!compact && "border-accent/50")}>
      <CardHeader title={<span className="inline-flex items-center gap-2"><FlaskConical className="h-4 w-4" />{t("carriers.demo.title")}</span>} subtitle={t("carriers.demo.sub")} action={<Badge tone="amber">{t("carriers.simulated")}</Badge>} />
      <form className="px-5 pb-5 space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (customerId && carrierId) go.mutate(); }}>
        {simulated.length > 1 ? (
          <Select label={t("common.carrier")} value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>{simulated.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
        ) : <p className="text-sm"><Building2 className="inline h-4 w-4 mr-1" />{simulated[0]?.name ?? "—"}</p>}
        {!initialCustomerId ? (
          <div className="relative">
            <Input label={t("carriers.demo.customer")} placeholder={t("carriers.demo.searchCustomer")} value={customerId ? (detail.data ? `${detail.data.customer.firstName} ${detail.data.customer.lastName}` : "…") : q} onChange={(e) => { setCustomerId(""); setPolicyId(""); setQ(e.target.value); }} />
            {!customerId && (search.data?.length ?? 0) > 0 ? (
              <ul className="absolute z-10 left-0 right-0 mt-1 rounded-2xl border border-ink/10 bg-white shadow-card overflow-hidden">
                {search.data!.map((c) => <li key={c.id}><button type="button" onClick={() => { setCustomerId(c.id); setQ(""); }} className="flex w-full justify-between px-4 min-h-tap items-center hover:bg-primary/8 text-left"><span className="font-semibold">{c.name}</span><span className="text-xs text-ink/55">{formatPhone(c.phone) ?? ""} · {c.policyCount} {t("common.policies").toLowerCase()}</span></button></li>)}
              </ul>
            ) : null}
          </div>
        ) : null}
        <Select label={t("carriers.demo.policy")} value={effectivePolicy} onChange={(e) => setPolicyId(e.target.value)} disabled={!customerId}>
          {policies.map((p) => <option key={p.id} value={p.id}>{p.carrier} · {p.line.replace(/_/g, " ")} · {p.policyNumber}</option>)}
          <option value="">{t("carriers.demo.noPolicy")}</option>
        </Select>
        <Select label={t("carriers.demo.kind")} value={kind} onChange={(e) => setKind(e.target.value as CarrierRequest["kind"])}>
          {(["policy_download", "endorsement", "renewal_quote", "new_business"] as const).map((k) => <option key={k} value={k}>{t(`carriers.kind.${k}`)}</option>)}
        </Select>
        <Button type="submit" className="w-full" disabled={!customerId || !carrierId || (kind !== "new_business" && !effectivePolicy)} loading={go.isPending}><Send className="h-4 w-4" />{t("carriers.demo.create")}</Button>
      </form>
    </Card>
  );
}

